import {createFileRoute} from '@tanstack/react-router';

import type {ClaimListScope} from '@/lib/list-scope';
import {parseIsoDay, parseNonEmpty, parsePage} from '@/lib/search-params';
import {AdvanceRequestListPage} from '@/modules/advance-requests/pages/AdvanceRequestListPage';
import {parseStatus} from '@/modules/advance-requests/lib/status-filter';
import type {AdvanceStatusFilter} from '@/modules/advance-requests/lib/status-filter';

export type AdvancesListTab =
  | 'all'
  // Advances sitting with YOU for a decision right now — not the same question
  // as `awaiting`, which is every submitted advance in scope (DERP-276).
  | 'needs_decision'
  | 'drafts'
  // Waiting on somebody else, from the requester's side: submitted, approved
  // and partially-approved all mean "not my move".
  | 'in_progress'
  | 'awaiting'
  | 'ready_to_pay'
  // Everything still moving, for someone who monitors rather than operates.
  | 'open'
  // Money out, not yet closed.
  | 'disbursed'
  | 'awaiting_receipt'
  // Paid, no proof ever owed, not yet closed — the queue a `:close` holder
  // can clear. The other half of `paid` from `awaiting_receipt`.
  | 'settled'
  // Receipts submitted and waiting on a holder of `advance_requests:close`
  // (DERP-232). Without a queue of its own the permission is unusable —
  // a verifier would have to hunt through All to find their own work.
  | 'to_verify'
  // Every ending in one tab. The pills still tell them apart, and grouping
  // them stops three terminal states each costing a slot.
  | 'finished'
  // Advances with a line that failed proof and is not yet recovered (the
  // owner, 2026-11-03). Not a status: any line may fail.
  | 'failure_of_proof';

const TAB_VALUES: ReadonlyArray<AdvancesListTab> = [
  'all',
  'needs_decision',
  'drafts',
  'in_progress',
  'awaiting',
  'ready_to_pay',
  'open',
  'disbursed',
  'awaiting_receipt',
  'settled',
  'to_verify',
  'finished',
  'failure_of_proof',
];

/**
 * Whose rows the list shows — the question that used to share the tab row with
 * "what state are they in" (DERP-276).
 *
 * Everyone / Mine for a holder of `advance_requests:view_all`, Mine / To approve for
 * an approver without it, Mine alone for anyone else -- `claimListScope`
 * decides which, and the page ignores a URL scope the viewer cannot have.
 */
export type AdvancesListScope = ClaimListScope;

const SCOPE_VALUES: ReadonlyArray<AdvancesListScope> = ['all', 'mine', 'approving'];

/**
 * URL state for the advance-requests index. Fields are optional in the
 * type — links from outside (dashboard, detail page) can navigate here
 * without specifying search params.
 *
 * `tab` and `scope` are deliberately left undefined rather than defaulted here:
 * both defaults depend on what the viewer may see, which this validator cannot
 * know. The page resolves them (`resolveTab` / the scope control) — see
 * `lib/list-tabs.ts` for why the landing tab is per-role.
 */
export interface AdvancesListSearch {
  tab?: AdvancesListTab;
  scope?: AdvancesListScope;
  /**
   * A single status, narrowing WITHIN the All tab.
   *
   * Deliberately not a third axis: every other tab already pins its statuses,
   * so a dropdown that intersected with them would produce views that read as
   * broken rather than narrow (Drafts filtered to Paid is empty, and correctly
   * so, which helps nobody). The page shows the control on All only, and drops
   * this param when you leave — a status left lurking behind a tab that also
   * filters by status is the conflation this row was split to remove.
   */
  status?: AdvanceStatusFilter;
  /** A SITE id — the filter is labelled by the site's SQL Account code (DERP-233). */
  site?: string;
  /** Inclusive SUBMITTED-date bounds, ISO `yyyy-MM-dd` (MYT days on the API). */
  from?: string;
  to?: string;
  /** Inclusive EXPECTED-PAYMENT-date bounds, ISO `yyyy-MM-dd`. */
  payFrom?: string;
  payTo?: string;
  q?: string;
  page?: number;
}

function parseTab(value: unknown): AdvancesListTab | undefined {
  return TAB_VALUES.find((t) => t === value);
}

function parseScope(value: unknown): AdvancesListScope | undefined {
  return SCOPE_VALUES.find((s) => s === value);
}

const validateAdvancesListSearch = (search: Record<string, unknown>): AdvancesListSearch => ({
  tab: parseTab(search.tab),
  scope: parseScope(search.scope),
  status: parseStatus(search.status),
  site: parseNonEmpty(search.site),
  from: parseIsoDay(search.from),
  to: parseIsoDay(search.to),
  payFrom: parseIsoDay(search.payFrom),
  payTo: parseIsoDay(search.payTo),
  q: parseNonEmpty(search.q),
  page: parsePage(search.page),
});

export const Route = createFileRoute('/_authenticated/advances/')({
  validateSearch: validateAdvancesListSearch,
  component: AdvanceRequestListPage,
});
